import { parseDocument } from 'yaml';

/**
 * Result of parsing the receipt-config editor buffer.
 *
 * `empty` covers a buffer with no YAML content at all — blank, or only
 * comments (the editor's own seed text). It is "nothing yet", not a parse
 * failure: no error banner, but nothing to save either (RL-366).
 */
export type ReceiptConfigYamlResult =
  | { kind: 'empty' }
  | { kind: 'ok'; value: Record<string, unknown> }
  | { kind: 'error'; error: string };

export function parseReceiptConfigYaml(text: string): ReceiptConfigYamlResult {
  let doc;
  try {
    doc = parseDocument(text);
  } catch (err) {
    return { kind: 'error', error: err instanceof Error ? err.message : String(err) };
  }
  const firstError = doc.errors[0];
  if (firstError) return { kind: 'error', error: firstError.message };
  // `contents` is null only when the document holds no node at all; an
  // explicit `null` / `~` scalar still yields a node and is rejected below.
  if (doc.contents === null) return { kind: 'empty' };
  const value: unknown = doc.toJS();
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return { kind: 'error', error: 'top-level value must be a YAML object' };
  }
  return { kind: 'ok', value: value as Record<string, unknown> };
}
